import React, { useRef, useCallback, useEffect } from "react";

/**
 * 3D Tactile Knob
 * Drag vertically to change value
 */
export default function Knob({
  value,
  min = 0,
  max = 1,
  step = 0.01,
  size = 56,
  label = "",
  unit = "",
  accentColor,
  onChange,
  "data-testid": testId,
  format,
}) {
  const knobRef = useRef(null);
  const startY = useRef(0);
  const startValue = useRef(value);

  const range = max - min;
  const normalized = (value - min) / range; // 0..1
  // Rotate from -135deg to +135deg (270deg total)
  const rotation = -135 + normalized * 270;

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    startY.current = e.clientY;
    startValue.current = value;

    const onMove = (ev) => {
      const dy = startY.current - ev.clientY;
      const delta = (dy / 150) * range;
      let next = startValue.current + delta;
      next = Math.round(next / step) * step;
      next = Math.max(min, Math.min(max, next));
      onChange(Number(next.toFixed(4)));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [value, min, max, range, step, onChange]);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const delta = -Math.sign(e.deltaY) * step * (e.shiftKey ? 10 : 1);
    let next = value + delta;
    next = Math.max(min, Math.min(max, next));
    onChange(Number(next.toFixed(4)));
  }, [value, min, max, step, onChange]);

  useEffect(() => {
    const el = knobRef.current;
    if (!el) return;
    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [handleWheel]);

  const display = format ? format(value) : `${value.toFixed(2)}${unit}`;

  return (
    <div className="flex flex-col items-center gap-1.5 select-none">
      <div
        ref={knobRef}
        className="knob"
        style={{
          width: size, height: size,
          "--accent-color": accentColor || "#00FF41",
        }}
        onMouseDown={handleMouseDown}
        data-testid={testId}
      >
        <div
          className="knob-indicator"
          style={{
            transform: `translateX(-50%) rotate(${rotation}deg)`,
            background: accentColor || "#00FF41",
            boxShadow: `0 0 6px ${accentColor || "#00FF41"}`,
          }}
        />
      </div>
      {label && <div className="label-mono">{label}</div>}
      <div className="font-mono text-[10px] text-white/80">{display}</div>
    </div>
  );
}
